/**
 * Cookie-backed filter persistence.
 * Stored as a single JSON cookie so all filter state travels together.
 *
 * Usage:
 *   const filters = getSavedFilters({ sports: [], domains: ["team"] });
 *   saveFilters({ ...filters, sports: ["Basketball"] });
 */

const COOKIE_NAME = "aau-filters";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days

function readCookie(name) {
    // Prefer localStorage — cookies are unreliable inside the preview iframe
    // (browsers block SameSite=Lax cookies in a cross-site iframe context).
    if (typeof window !== "undefined") {
        try {
            const ls = window.localStorage.getItem(name);
            if (ls != null) return ls;
        } catch { /* localStorage blocked — fall through to cookie */ }
    }
    if (typeof document === "undefined") return "";
    const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.slice(name.length + 1)) : "";
}

function writeCookie(name, value) {
    if (typeof window !== "undefined") {
        try { window.localStorage.setItem(name, value); } catch { /* ignore quota / blocked */ }
    }
    if (typeof document === "undefined") return;
    try {
        document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
    } catch { /* ignore */ }
}

export function getSavedFilters(defaults = {}) {
    try {
        const raw = readCookie(COOKIE_NAME);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        return { ...defaults, ...parsed };
    } catch {
        return defaults;
    }
}

export function saveFilters(filters) {
    try {
        writeCookie(COOKIE_NAME, JSON.stringify(filters));
    } catch {
        // ignore quota / disabled cookies
    }
}

// List-view control prefs (sort / direction / page size), persisted separately.
const LIST_PREFS_COOKIE = "aau-list-prefs";

export function getListPrefs(defaults = {}) {
    try {
        const raw = readCookie(LIST_PREFS_COOKIE);
        if (!raw) return defaults;
        return { ...defaults, ...JSON.parse(raw) };
    } catch {
        return defaults;
    }
}

export function saveListPrefs(prefs) {
    try {
        writeCookie(LIST_PREFS_COOKIE, JSON.stringify(prefs));
    } catch {
        // ignore
    }
}

// The last valid 5-digit ZIP the user searched near (from saved filters).
// Used by detail pages to show distance info relative to their search ZIP.
export function savedNearZip() {
    const z = (getSavedFilters({}).near_zip || "").toString().trim();
    return /^\d{5}$/.test(z) ? z : "";
}
